/*
 * <variant-stock-filter>
 *
 * Visual-only filter for the product variant picker. When the toggle is
 * enabled, variant option values that only lead to out-of-stock variants
 * (Shopify inventory_quantity <= 0) are completely hidden.
 *
 * This never changes Shopify inventory settings — it reads an availability
 * snapshot (in_stock = inventory_quantity > 0) rendered into a JSON <script>
 * and toggles a CSS class on the option inputs/options.
 *
 * Dawn replaces the <variant-selects> element on every variant change, so the
 * filter re-applies itself via a MutationObserver on the surrounding
 * <product-info>. The toggle element lives outside <variant-selects>, so its
 * checked state persists across those re-renders.
 */
class VariantStockFilter extends HTMLElement {
  constructor() {
    super();
    this.HIDDEN_CLASS = 'variant-stock-filter-hidden';
  }

  connectedCallback() {
    this.productInfo = this.closest('product-info');
    this.checkbox = this.querySelector('.variant-stock-filter__checkbox');
    this.variants = this.#parseVariants();

    if (!this.checkbox || !this.variants.length) return;

    this.checkbox.addEventListener('change', () => this.#onToggle());
    this.#observeVariantSelects();

    // A default-checked box fires no change event, so apply the filter on load.
    if (this.enabled) this.#onToggle();
  }

  disconnectedCallback() {
    this.observer?.disconnect();
    this._settleObserver?.disconnect();
    clearTimeout(this._settleTimer);
    clearTimeout(this._filterCapTimer);
  }

  get enabled() {
    return !!this.checkbox?.checked;
  }

  get variantSelects() {
    return this.productInfo?.querySelector('variant-selects');
  }

  #parseVariants() {
    try {
      const raw = this.querySelector('.variant-stock-filter__data')?.textContent;
      const data = JSON.parse(raw);
      return Array.isArray(data) ? data : [];
    } catch (error) {
      console.warn('variant-stock-filter: could not parse variant data', error);
      return [];
    }
  }

  // Re-apply the filter whenever Dawn swaps in a fresh <variant-selects>.
  // Mutations arrive in bursts during a re-render, so coalesce them.
  #observeVariantSelects() {
    if (!this.productInfo) return;
    let queued = false;
    this.observer = new MutationObserver(() => {
      this.#updateBuyButtonState(
        this.enabled && !this.variants.some((variant) => variant.in_stock)
      );
      if (!this.enabled || queued) return;
      queued = true;
      // Microtask (not rAF): the hide must land before the selection overlay's
      // own rAF measures the pills, otherwise it captures the pre-filter layout
      // and the outline ends up detached.
      queueMicrotask(() => {
        queued = false;
        if (!this.enabled) return;

        if (this._togglePending) {
          // Re-render triggered by the toggle auto-select: it re-expanded the
          // pills unfiltered, so keep transitions frozen / overlay hidden,
          // re-hide, and reveal only once the shrinking layout has settled.
          const pendingMode = this._togglePending;
          this._togglePending = false;
          if (pendingMode === 'suppress') this.#beginFiltering();
          this.#applyFilter();
          if (pendingMode === 'suppress') {
            this.#scheduleSettle();
          } else {
            this.#refreshOverlay();
          }
        } else {
          // Manual variant change: just re-hide the out-of-stock pills. Run it
          // in this microtask (before the overlay's rAF) so the outline still
          // measures the filtered layout, while leaving the draw animation
          // intact for the genuine user selection.
          this.#applyFilter();
          const lastChanged = this.#ensureVisibleSelection();
          if (lastChanged) {
            this._togglePending = 'quiet';
            this.#suppressNextPillFocus();
            this.#dispatchInternalChange(lastChanged);
          }
        }
      });
    });
    this.observer.observe(this.productInfo, { childList: true, subtree: true });
  }

  #onToggle() {
    this.#updateEmptyState();

    // 1. Freeze all pill transitions/animations and hide the floating overlay.
    this.#beginFiltering();

    if (this.enabled) {
      // 2. Pick the first visible (in-stock) option in each group, left→right,
      //    then 3. apply it instantly. Setting the new radio/option clears the
      //    old selected styling for us.
      const chosen = this.#firstVisibleCombination();
      const lastChanged = this.#applySelection(chosen);

      // 4. Hide the non-in-stock pills (now relative to the valid selection).
      this.#applyFilter();

      if (lastChanged) {
        // 5. Tell Dawn so price/delivery/variant id update. This re-renders the
        //    pills unfiltered; the observer (seeing _togglePending) re-hides them
        //    and settles the window, so do NOT settle here or we'd reveal before
        //    the async re-render.
        this._togglePending = 'suppress';
        this.#suppressNextPillFocus();
        this.#dispatchInternalChange(lastChanged);
        return;
      }
    } else {
      this.#applyFilter();
    }

    // 6. Purely local change (no re-render): reveal once the layout settles.
    this.#scheduleSettle();
  }

  #updateEmptyState() {
    const hasNoInStockVariants = this.enabled && !this.variants.some((variant) => variant.in_stock);
    this.productInfo?.classList.toggle('has-no-in-stock-variants', hasNoInStockVariants);
    this.#updateBuyButtonState(hasNoInStockVariants);
  }

  #updateBuyButtonState(hasNoInStockVariants) {
    const button = this.productInfo?.querySelector('.product-form__submit[name="add"]');
    const label = button?.querySelector('span');
    if (!button || !label) return;

    if (hasNoInStockVariants) {
      if (!button.hasAttribute('data-stock-filter-disabled')) {
        button.dataset.stockFilterDisabled = button.disabled ? 'true' : 'false';
        button.dataset.stockFilterLabel = label.textContent.trim();
      }
      button.disabled = true;
      if (label.textContent.trim() !== 'Momentan nicht auf Lager') {
        label.textContent = 'Momentan nicht auf Lager';
      }
      return;
    }

    if (!button.hasAttribute('data-stock-filter-disabled')) return;
    button.disabled = button.dataset.stockFilterDisabled === 'true';
    label.textContent = button.dataset.stockFilterLabel;
    delete button.dataset.stockFilterDisabled;
    delete button.dataset.stockFilterLabel;
  }

  // First visible option per group, chosen left→right so the running combination
  // always stays on an in-stock variant (avoids landing on an unavailable combo).
  #firstVisibleCombination() {
    const groups = this.#groups();
    const chosen = [];
    for (let position = 0; position < groups.length; position++) {
      const values = this.#valueElements(groups[position]);
      const pick =
        values.find(({ value }) =>
          this.variants.some(
            (variant) =>
              variant.in_stock &&
              variant.options[position] === value &&
              chosen.every((chosenValue, index) => variant.options[index] === chosenValue)
          )
        ) || values[0];
      chosen[position] = pick ? pick.value : null;
    }
    return chosen;
  }

  // Apply a chosen value per group to the DOM. Returns the last element changed
  // (so the caller can dispatch a single change), or null if nothing changed.
  #applySelection(chosen) {
    let lastChanged = null;
    this.#groups().forEach((group, position) => {
      const value = chosen[position];
      if (value == null) return;

      const select = this.#selectElement(group);
      if (select) {
        if (select.value !== value) {
          Array.from(select.options).forEach((option) => option.removeAttribute('selected'));
          const option = Array.from(select.options).find((opt) => opt.value === value);
          if (option) {
            option.setAttribute('selected', 'selected');
            select.value = value;
            lastChanged = select;
          }
        }
      } else {
        const input = Array.from(group.querySelectorAll('input[type="radio"]')).find(
          (radio) => radio.value === value
        );
        if (input && !input.checked) {
          input.checked = true;
          lastChanged = input;
        }
      }
    });
    return lastChanged;
  }

  // A no-op childList change makes the selection-overlay observer recompute the
  // outline against the now-filtered pill layout. Detach our own observer first
  // so this doesn't re-enter it.
  #refreshOverlay() {
    const variantSelects = this.variantSelects;
    if (!variantSelects) return;
    this.observer?.disconnect();
    const marker = document.createTextNode('');
    variantSelects.appendChild(marker);
    marker.remove();
    if (this.productInfo) {
      this.observer?.observe(this.productInfo, { childList: true, subtree: true });
    }
  }

  // Freeze transitions/animations and hide the floating selection overlay on
  // the whole product-info while pills are hidden/shown and re-selected.
  #beginFiltering() {
    const root = this.productInfo;
    if (!root) return;
    root.classList.add('is-filtering-variants');
    // Absolute cap so the suppressor is never left on if nothing else settles it.
    clearTimeout(this._filterCapTimer);
    this._filterCapTimer = setTimeout(() => this.#finishFiltering(), 1500);
  }

  // Reveal only after the pill layout stops changing. The auto-select re-render
  // re-expands the pills (full, unfiltered) and our hide then shrinks them, so
  // the height moves over an async, variable window — watch it until it's stable.
  #scheduleSettle() {
    const root = this.productInfo;
    if (!root) return;

    const settle = () => {
      clearTimeout(this._settleTimer);
      this._settleTimer = setTimeout(() => this.#finishFiltering(), 120);
    };

    this._settleObserver?.disconnect();
    this._settleObserver = new ResizeObserver(settle);
    this._settleObserver.observe(root);
    settle();
  }

  // Final step: stop watching, recompute the overlay against the settled layout,
  // then restore transitions/visibility on the next frame.
  #finishFiltering() {
    clearTimeout(this._settleTimer);
    clearTimeout(this._filterCapTimer);
    this._settleObserver?.disconnect();
    this._settleObserver = null;
    this._togglePending = false;

    this.#refreshOverlay();
    // Two frames: frame 1 lets the overlay recompute its path against the
    // settled layout (still hidden); frame 2 reveals it already in position.
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        this.productInfo?.classList.remove('is-filtering-variants');
      });
    });
  }

  #applyFilter() {
    const groups = this.#groups();
    if (!groups.length) return;

    const enabled = this.enabled;
    const selected = groups.map((group) => this.#selectedValue(group));

    groups.forEach((group, position) => {
      this.#valueElements(group).forEach(({ element, value }) => {
        const show = !enabled || this.#hasInStock(position, value, selected);
        this.#setHidden(element, !show);
      });
    });
  }

  #ensureVisibleSelection() {
    let lastChanged = null;

    this.#groups().forEach((group) => {
      const selected = this.#selectedElement(group);
      if (selected && !selected.classList.contains(this.HIDDEN_CLASS)) return;

      const firstVisible = this.#valueElements(group).find(({ element }) => !element.classList.contains(this.HIDDEN_CLASS));
      if (!firstVisible) return;

      const select = this.#selectElement(group);
      if (select) {
        if (select.value !== firstVisible.value) {
          Array.from(select.options).forEach((option) => option.removeAttribute('selected'));
          firstVisible.element.setAttribute('selected', 'selected');
          select.value = firstVisible.value;
          lastChanged = select;
        }
      } else if (!firstVisible.element.checked) {
        firstVisible.element.checked = true;
        lastChanged = firstVisible.element;
      }
    });

    return lastChanged;
  }

  // An option value is shown if at least one in-stock variant uses it at this
  // position while matching the selected values from earlier option groups.
  // This keeps the first group reachable, then cascades later groups from it.
  #hasInStock(position, value, selected) {
    return this.variants.some((variant) => {
      if (!variant.in_stock) return false;
      if (variant.options[position] !== value) return false;
      return selected.every(
        (selectedValue, index) => index >= position || selectedValue == null || variant.options[index] === selectedValue
      );
    });
  }

  // Dawn focuses the changed option after its re-fetch (product-info.js), which
  // triggers :focus-visible. Blur that focus once since the change wasn't a
  // user interaction. A timeout cleans up if no focus arrives.
  #suppressNextPillFocus() {
    const root = this.productInfo || document;
    const handler = (event) => {
      const target = event.target;
      if (target && target.matches && target.matches('input[type="radio"]')) {
        target.blur();
        cleanup();
      }
    };
    const cleanup = () => {
      clearTimeout(timer);
      root.removeEventListener('focusin', handler, true);
    };
    const timer = setTimeout(cleanup, 1500);
    root.addEventListener('focusin', handler, true);
  }

  #dispatchInternalChange(element) {
    const event = new Event('change', { bubbles: true });
    event.variantStockFilterInternal = true;
    element.dispatchEvent(event);
  }

  // Option groups in DOM order; their index maps to the option position.
  #groups() {
    const variantSelects = this.variantSelects;
    if (!variantSelects) return [];
    return Array.from(
      variantSelects.querySelectorAll('fieldset.product-form__input, .product-form__input--dropdown')
    );
  }

  #selectElement(group) {
    return group.tagName === 'SELECT' ? group : group.querySelector('select');
  }

  #selectedValue(group) {
    const select = this.#selectElement(group);
    if (select) return select.value;
    return group.querySelector('input[type="radio"]:checked')?.value ?? null;
  }

  #selectedElement(group) {
    const select = this.#selectElement(group);
    if (select) return select.selectedOptions[0] ?? null;
    return group.querySelector('input[type="radio"]:checked');
  }

  #valueElements(group) {
    const select = this.#selectElement(group);
    if (select) {
      return Array.from(select.options).map((option) => ({ element: option, value: option.value }));
    }
    return Array.from(group.querySelectorAll('input[type="radio"]')).map((input) => ({
      element: input,
      value: input.value,
    }));
  }

  #setHidden(element, hidden) {
    element.classList.toggle(this.HIDDEN_CLASS, hidden);
    if (element.tagName === 'OPTION') element.hidden = hidden;
  }
}

if (!customElements.get('variant-stock-filter')) {
  customElements.define('variant-stock-filter', VariantStockFilter);
}
